import React, { useState, useEffect } from 'react';
import type { GameRTPComponent } from '../../../types/builder';

interface GameRTPRendererProps {
  component: GameRTPComponent;
}

export const GameRTPRenderer: React.FC<GameRTPRendererProps> = ({ component }) => {
  const { settings } = component;
  const [displayGames, setDisplayGames] = useState(settings.games);

  useEffect(() => {
    if (!settings.animateRTP) {
      setDisplayGames(settings.games);
      return;
    }

    const interval = setInterval(() => {
      setDisplayGames((prevGames) =>
        prevGames.map((game) => {
          const fluctuation = settings.rtpFluctuation || 0.5;
          const randomChange = (Math.random() * fluctuation * 2) - fluctuation;
          const newRTP = Math.min(100, Math.max(0, game.rtp + randomChange));
          return {
            ...game,
            rtp: parseFloat(newRTP.toFixed(1)),
          };
        })
      );
    }, 2500);

    return () => clearInterval(interval);
  }, [settings.animateRTP, settings.rtpFluctuation]);

  // Sync with settings when games change
  useEffect(() => {
    setDisplayGames(settings.games);
  }, [settings.games]);

  return (
    <div
      style={{
        backgroundColor: settings.backgroundColor,
        color: settings.textColor,
        padding: `${settings.padding}px`,
      }}
    >
      <div className="max-w-4xl mx-auto">
        {settings.title && (
          <h2 className="text-lg font-bold mb-4">{settings.title}</h2>
        )}
        
        <div className="space-y-3">
          {displayGames.map((game) => (
            <div
              key={game.id}
              className="rounded-lg overflow-hidden"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
            >
              <div className="flex items-center gap-3 p-3">
                <img
                  src={game.banner}
                  alt={game.name}
                  className="w-20 h-12 object-cover rounded flex-shrink-0"
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-sm truncate">{game.name}</h3>
                    <span
                      className="text-sm font-bold ml-2 flex-shrink-0 transition-all duration-300"
                      style={{ color: settings.progressColor }}
                    >
                      {game.rtp}%
                    </span>
                  </div>
                  
                  {settings.showProvider && (
                    <p className="text-xs opacity-70 mb-1">{game.provider}</p>
                  )}
                  
                  <div
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: settings.progressBackgroundColor }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${game.rtp}%`,
                        backgroundColor: settings.progressColor,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

